import React, { useRef } from 'react';
import { Rnd } from 'react-rnd';
import { CanvasElement } from './types';
import { Barcode as BarcodeIcon, QrCode as QrCodeIcon, Hash } from 'lucide-react';
import ReactBarcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';

interface CanvasProps {
  elements: CanvasElement[];
  setElements: React.Dispatch<React.SetStateAction<CanvasElement[]>>;
  selectedElementId: string | null;
  setSelectedElementId: (id: string | null) => void;
  widthMm: number;
  heightMm: number;
  readOnly?: boolean;
  sampleData?: Record<string, any>;
  zoom?: number;
  calculatedVariables?: any[];
}

const MM_TO_PX = 3.78; // Approximate conversion for screen display

export const Canvas: React.FC<CanvasProps> = ({
  elements,
  setElements,
  selectedElementId,
  setSelectedElementId,
  widthMm,
  heightMm,
  readOnly = false,
  sampleData,
  zoom = 1,
  calculatedVariables = []
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);

  const updateElement = (id: string, updates: Partial<CanvasElement>) => {
    setElements(prev => prev.map(el => el.id === id ? { ...el, ...updates } : el));
  };

  const CURRENT_MM_TO_PX = MM_TO_PX * zoom;
  const pxWidth = Math.round(widthMm * CURRENT_MM_TO_PX);
  const pxHeight = Math.round(heightMm * CURRENT_MM_TO_PX);

  // --- Rules Engine ---
  const evaluateCondition = (data: any, field: string, operator: string, value: any): boolean => {
    const actualValue = data[field];
    if (operator === 'isEmpty') return actualValue === undefined || actualValue === null || actualValue === '';
    if (operator === 'isNotEmpty') return actualValue !== undefined && actualValue !== null && actualValue !== '';
    if (actualValue === undefined || actualValue === null) return false;
    
    const actualStr = String(actualValue).toLowerCase();
    const targetStr = String(value).toLowerCase();

    switch (operator) {
      case 'equals': return actualStr === targetStr;
      case 'notEquals': return actualStr !== targetStr;
      case 'startsWith': return actualStr.startsWith(targetStr);
      case 'contains': return actualStr.includes(targetStr);
      case 'hasItemWithSku':
        if (Array.isArray(actualValue)) {
          return actualValue.some((item: any) => item?.sku && String(item.sku).toLowerCase().includes(targetStr));
        }
        return false;
      default: return false;
    }
  };

  const evaluateRules = (rules: any[], data: any, defaultValue: string) => {
    for (const rule of rules) {
      let isMatch = false;
      if (!rule.conditions || rule.conditions.length === 0) {
        isMatch = true;
      } else if (rule.logic === 'OR') {
        isMatch = rule.conditions.some((c: any) => evaluateCondition(data, c.field, c.operator, c.value));
      } else {
        isMatch = rule.conditions.every((c: any) => evaluateCondition(data, c.field, c.operator, c.value));
      }
      if (isMatch) return rule.result;
    }
    return defaultValue;
  };

  let dataToUse = sampleData?.data && typeof sampleData.data === 'object' ? sampleData.data : sampleData || {};
  if (Array.isArray(dataToUse)) dataToUse = dataToUse[0] || {};

  const flattenObject = (obj: any, prefix = ''): Record<string, any> => {
    if (!obj) return {};
    return Object.keys(obj).reduce((acc: Record<string, any>, k: string) => {
      const pre = prefix.length ? prefix + '.' : '';
      if (typeof obj[k] === 'object' && obj[k] !== null && !Array.isArray(obj[k])) {
        Object.assign(acc, flattenObject(obj[k], pre + k));
      } else {
        acc[pre + k] = obj[k];
      }
      return acc;
    }, {});
  };

  const flatData = React.useMemo(() => {
    const base = flattenObject(dataToUse);
    if (readOnly && calculatedVariables && calculatedVariables.length > 0) {
      for (const calcVar of calculatedVariables) {
        base[calcVar.name] = evaluateRules(calcVar.rules || [], base, calcVar.defaultValue);
      }
    }
    return base;
  }, [dataToUse, readOnly, calculatedVariables]);

  const replaceVariables = (text: string, contextData?: any) => {
    if (!text) return text;
    let result = text;
    const dataSource = contextData ? flattenObject(contextData) : flatData;
    
    result = result.replace(/{{(.*?)}}/g, (match, expression) => {
      let parts = expression.split('|');
      const cleanKey = parts[0].trim();
      
      if (cleanKey === 'TARIH') return new Date().toLocaleDateString('tr-TR');
      if (cleanKey === 'SAAT') return new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

      let val = dataSource[cleanKey];
      if (val !== undefined && val !== null && val !== '') {
        let strVal = String(val);
        for (let i = 1; i < parts.length; i++) {
          const funcPart = parts[i].trim();
          if (funcPart.startsWith('first:')) {
            const count = parseInt(funcPart.split(':')[1], 10);
            if (!isNaN(count)) strVal = strVal.substring(0, count);
          } else if (funcPart.startsWith('last:')) {
            const count = parseInt(funcPart.split(':')[1], 10);
            if (!isNaN(count)) strVal = strVal.substring(Math.max(0, strVal.length - count));
          } else if (funcPart === 'upper') {
            strVal = strVal.toLocaleUpperCase('tr-TR');
          } else if (funcPart === 'lower') {
            strVal = strVal.toLocaleLowerCase('tr-TR');
          }
        }
        return strVal;
      }
      return match;
    });

    return result;
  };

  const renderElementContent = (element: CanvasElement, contentW?: number, contentH?: number) => {
    // Tasarım sırasında (readOnly false iken) ve sampleValue girilmişse onu göster.
    // Ancak mobil izleme (readOnly true) durumunda gerçek veriyi basacağımız için sampleValue yerine content'i çözmeliyiz.
    // readOnly durumunda da preview mode olabilir ama normalde veriler sampleData üzerinden gelir.
    const sourceContent = (!readOnly && element.sampleValue) ? element.sampleValue : element.content;
    const displayContent = replaceVariables(sourceContent);

    switch (element.type) {
      case 'TEXT':
        return (
          <div 
            style={{ 
              fontSize: `${(element.fontSize || 12) * zoom}pt`,
              fontWeight: element.fontWeight || 'normal',
              fontFamily: 'Arial, Helvetica, sans-serif',
              width: '100%',
              height: '100%',
              display: (element.maxLines && element.maxLines > 1) ? '-webkit-box' : 'flex',
              WebkitBoxOrient: (element.maxLines && element.maxLines > 1) ? 'vertical' : undefined,
              WebkitLineClamp: (element.maxLines && element.maxLines > 1) ? element.maxLines : undefined,
              alignItems: (element.maxLines && element.maxLines > 1) ? undefined : (element.verticalAlign === 'top' ? 'flex-start' : (element.verticalAlign === 'bottom' ? 'flex-end' : 'center')),
              justifyContent: (element.maxLines && element.maxLines > 1) ? undefined : (element.textAlign === 'center' ? 'center' : (element.textAlign === 'right' ? 'flex-end' : 'flex-start')),
              textAlign: element.textAlign as any || 'left',
              wordBreak: 'break-word',
              lineHeight: 1.2,
              color: '#000000',
              borderWidth: element.borderWidth ? `${element.borderWidth * zoom}px` : '0px',
              borderStyle: element.borderStyle || 'solid',
              borderColor: '#000000',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
          >
            {displayContent}
          </div>
        );
      case 'BARCODE':
        const barcodeH = contentH ? contentH * 0.75 : (element.height ? element.height * CURRENT_MM_TO_PX * 0.75 : 35 * zoom);
        return (
          <div className="w-full h-full flex flex-col items-center justify-center pointer-events-none [&>svg]:max-w-full [&>svg]:max-h-full [&>svg]:w-auto [&>svg]:h-auto">
             {displayContent ? (
               <ReactBarcode 
                 value={displayContent} 
                 width={1.4 * zoom}
                 height={barcodeH}
                 fontSize={11 * zoom}
                 displayValue={element.showBarcodeText !== false}
                 margin={0}
                 background="transparent"
               />
             ) : (
               <div className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-gray-400 bg-gray-50 text-gray-500 p-1">
                 <BarcodeIcon className="w-full h-2/3 opacity-50" preserveAspectRatio="none" />
                 <span className="text-[10px] truncate w-full text-center mt-1" style={{ fontSize: `${10 * zoom}px` }}>Örnek Barkod</span>
               </div>
             )}
          </div>
        );
      case 'QR':
        const qrSize = Math.min(contentW || (element.width || 20) * CURRENT_MM_TO_PX, contentH || (element.height || 20) * CURRENT_MM_TO_PX) - 4;
        return (
          <div className="w-full h-full flex items-center justify-center p-1 relative">
            {displayContent ? (
              <QRCodeSVG 
                value={displayContent} 
                size={Math.max(10, qrSize)}
                level="L"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-gray-400 bg-gray-50 text-gray-500 p-1 relative">
                <QrCodeIcon className="w-full h-full opacity-50" />
                <span className="absolute text-[8px] bg-white/80 px-1 truncate max-w-full" style={{ fontSize: `${8 * zoom}px` }}>Örnek QR</span>
              </div>
            )}
          </div>
        );
      case 'BOX':
        return (
          <div 
            style={{ 
              width: '100%', 
              height: '100%', 
              borderWidth: `${(element.borderWidth || 1) * zoom}px`, 
              borderStyle: element.borderStyle || 'solid', 
              borderColor: '#000000',
              boxSizing: 'border-box'
            }} 
          />
        );
      case 'LIST':
        if (readOnly) {
          const itemsSource = element.listConfig?.itemSource || 'items';
          const items = flatData[itemsSource];
          if (!Array.isArray(items)) {
            return <div className="text-red-500 text-[10px] w-full text-center mt-2">Geçersiz kaynak: {itemsSource}</div>;
          }

          const columns = element.listConfig?.columns || [];
          const showHeader = element.listConfig?.showHeader !== false;
          
          const truncateText = (text: string, colWidthMm: number, fontSizePt: number = 10): string => {
            if (!text) return "";
            const str = String(text);
            const avgCharWidthMm = (fontSizePt * 0.352778) * 0.6;
            const maxChars = Math.max(1, Math.floor(colWidthMm / avgCharWidthMm));
            
            if (str.length > maxChars) {
              if (maxChars > 3) return str.substring(0, maxChars - 3) + "...";
              return str.substring(0, maxChars);
            }
            return str;
          };
          
            return (
            <div className="w-full h-full flex flex-col overflow-hidden">
              {showHeader && columns.length > 0 && (
                <div className="flex w-full font-bold border-b border-black" style={{ minHeight: `${(element.listConfig?.rowHeight || 10) * CURRENT_MM_TO_PX}px` }}>
                  {columns.map(col => (
                    <div 
                      key={`h-${col.id}`} 
                      style={{ 
                        width: `${col.width * CURRENT_MM_TO_PX}px`, 
                        flexShrink: 0, 
                        padding: '0 2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: col.align === 'center' ? 'center' : col.align === 'right' ? 'flex-end' : 'flex-start',
                        fontSize: `${(element.fontSize || 10) * zoom}pt`,
                        overflow: 'hidden'
                      }}
                    >
                      {truncateText(col.header || '', col.width, element.fontSize || 10)}
                    </div>
                  ))}
                </div>
              )}
              {items.map((item, i) => (
                <div key={i} className="flex w-full" style={{ minHeight: `${(element.listConfig?.rowHeight || 10) * CURRENT_MM_TO_PX}px` }}>
                  {columns.map(col => {
                    // For replaceVariables inside lists, we provide the item data wrapped in { item: ... } 
                    // so {{item.sku}} works.
                    const cellContent = replaceVariables(col.content, { item });
                    return (
                      <div 
                        key={col.id} 
                        style={{ 
                          width: `${col.width * CURRENT_MM_TO_PX}px`, 
                          flexShrink: 0, 
                          padding: '0 2px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: col.align === 'center' ? 'center' : col.align === 'right' ? 'flex-end' : 'flex-start',
                          fontSize: `${(element.fontSize || 10) * zoom}pt`,
                          fontWeight: element.fontWeight,
                          overflow: 'hidden'
                        }}
                      >
                        {truncateText(cellContent || '', col.width, element.fontSize || 10)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        }

        return (
          <div className="w-full h-full flex flex-col border border-dashed border-primary bg-primary/5 text-primary text-xs overflow-hidden relative">
            <div className="flex items-center justify-center p-1 bg-primary/20 font-bold border-b border-dashed border-primary">
              <Hash className="w-3 h-3 mr-1" />
              Tekrarlayan Liste
            </div>
            <div className="flex-1 flex flex-col items-center justify-center p-2 text-center opacity-70">
              <p>Öğe Listesi</p>
              <p className="text-[10px] mt-1">(Veriler alt alta tekrarlanacak)</p>
            </div>
          </div>
        );
      case 'LINE':
        return (
          <div 
            style={{ 
              width: '100%', 
              height: 0,
              borderTopWidth: `${(element.borderWidth || 1) * zoom}px`, 
              borderTopStyle: element.borderStyle || 'solid', 
              borderColor: '#000000',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)'
            }} 
          />
        );
      case 'VLINE':
        return (
          <div 
            style={{ 
              height: '100%', 
              width: 0,
              borderLeftWidth: `${(element.borderWidth || 1) * zoom}px`, 
              borderLeftStyle: element.borderStyle || 'solid', 
              borderColor: '#000000',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)'
            }} 
          />
        );
      default:
        return null;
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const startScroll = useRef({ left: 0, top: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Sadece sol tık
    if (containerRef.current) {
      isDragging.current = true;
      startPos.current = { x: e.pageX, y: e.pageY };
      startScroll.current = { 
        left: containerRef.current.scrollLeft, 
        top: containerRef.current.scrollTop 
      };
      containerRef.current.style.cursor = 'grabbing';
      containerRef.current.style.userSelect = 'none';
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    e.preventDefault();
    const dx = e.pageX - startPos.current.x;
    const dy = e.pageY - startPos.current.y;
    containerRef.current.scrollLeft = startScroll.current.left - dx;
    containerRef.current.scrollTop = startScroll.current.top - dy;
  };

  const handleMouseUpOrLeave = () => {
    isDragging.current = false;
    if (containerRef.current) {
      containerRef.current.style.cursor = '';
      containerRef.current.style.userSelect = '';
    }
  };

  return (
    <div 
      ref={containerRef}
      className="flex-1 bg-bgAlt rounded-lg overflow-auto border border-borderColor shadow-inner"
      onClick={() => { if (!readOnly) setSelectedElementId(null) }} // Click outside to deselect
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUpOrLeave}
      onMouseLeave={handleMouseUpOrLeave}
    >
      <div className="min-w-max min-h-max p-4 md:p-8 flex justify-center items-start">
        <div 
          ref={canvasRef}
          className="relative bg-white shadow-lg overflow-hidden transition-all duration-300 text-black"
          style={{ 
            width: `${pxWidth}px`, 
            height: `${pxHeight}px`,
            minWidth: `${pxWidth}px`,
            minHeight: `${pxHeight}px`
          }}
        >
          {elements.map((el) => {
            const isSelected = el.id === selectedElementId;
            const isOrthogonal = el.rotation === 90 || el.rotation === 270;
            const outerW = el.width ? el.width * CURRENT_MM_TO_PX : 100;
            const outerH = el.height ? el.height * CURRENT_MM_TO_PX : 40;
            const innerW = isOrthogonal ? outerH : outerW;
            const innerH = isOrthogonal ? outerW : outerH;

            if (readOnly) {
              // VisibleIf Evaluation
              let isVisible = true;
              if (el.visibleIf && el.visibleIf.length > 0) {
                 const logic = el.visibleIfLogic || 'AND';
                 if (logic === 'OR') {
                   isVisible = el.visibleIf.some(c => evaluateCondition(flatData, c.field, c.operator, c.value));
                 } else {
                   isVisible = el.visibleIf.every(c => evaluateCondition(flatData, c.field, c.operator, c.value));
                 }
              }
              if (!isVisible) return null;

              return (
                <div
                  key={el.id}
                  style={{
                    position: 'absolute',
                    left: el.x * CURRENT_MM_TO_PX,
                    top: el.y * CURRENT_MM_TO_PX,
                    width: `${outerW}px`,
                    height: `${outerH}px`,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${innerW}px`,
                      height: `${innerH}px`,
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: `translate(-50%, -50%) ${el.rotation ? `rotate(${el.rotation}deg)` : ''}`,
                      transformOrigin: 'center center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {renderElementContent(el, innerW, innerH)}
                  </div>
                </div>
              );
            }

            return (
              <Rnd
                key={el.id}
                bounds="parent"
                size={{ 
                  width: outerW, 
                  height: outerH 
                }}
                position={{ x: el.x * CURRENT_MM_TO_PX, y: el.y * CURRENT_MM_TO_PX }}
                onDragStop={(e, d) => {
                  updateElement(el.id, { 
                    x: Math.round(d.x / CURRENT_MM_TO_PX), 
                    y: Math.round(d.y / CURRENT_MM_TO_PX) 
                  });
                }}
                onResizeStop={(e, direction, ref, delta, position) => {
                  updateElement(el.id, {
                    width: Math.round(ref.offsetWidth / CURRENT_MM_TO_PX),
                    height: Math.round(ref.offsetHeight / CURRENT_MM_TO_PX),
                    x: Math.round(position.x / CURRENT_MM_TO_PX),
                    y: Math.round(position.y / CURRENT_MM_TO_PX)
                  });
                }}
                onClick={(e: React.MouseEvent) => {
                  if (readOnly) return;
                  e.stopPropagation();
                  setSelectedElementId(el.id);
                }}
                className={`group cursor-move ${isSelected ? 'ring-2 ring-primary ring-offset-1 z-10' : 'hover:ring-1 hover:ring-primary/50 z-0'}`}
                style={{
                  border: isSelected ? '1px dashed #6366f1' : '1px solid transparent',
                  overflow: 'hidden',
                  position: 'absolute',
                }}
                enableResizing={{
                  top: isSelected, right: isSelected, bottom: isSelected, left: isSelected,
                  topRight: isSelected, bottomRight: isSelected, bottomLeft: isSelected, topLeft: isSelected
                }}
              >
                <div
                  style={{
                    width: `${innerW}px`,
                    height: `${innerH}px`,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(-50%, -50%) ${el.rotation ? `rotate(${el.rotation}deg)` : ''}`,
                    transformOrigin: 'center center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  {renderElementContent(el, innerW, innerH)}
                </div>
              </Rnd>
            );
          })}
        </div>
      </div>
    </div>
  );
};
